import { Heading } from '@chakra-ui/react';
import {
  ICustomFieldResSchema,
  IUsageEvent,
  IUsageTypeResSchema,
} from '@monetize/types/app';
import { UploadIcon } from '@monetize/ui/icons';
import { formatNumber, toDateTimeShort } from '@monetize/utils/core';
import { createColumnHelper } from '../../../../components/Monetize/DataTableNew/appTable';
import { fromUnixTime } from 'date-fns/fromUnixTime';
import { FC, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import {
  MBox,
  MButton,
  MFlex,
  MFormField,
  MPageContainer,
  MPageHeader,
  MPageLoader,
  MText,
} from '../../../../components/Monetize';
import DataTableActions from '../../../../components/Monetize/DataTable/MDataTableActions';
import { DataTable } from '../../../../components/Monetize/DataTableNew/DataTable';
import { getColHeaderWithSize } from '../../../../components/Monetize/DataTableNew/tableUtilsNew';
import { DatePicker } from '../../../../components/Monetize/DatePicker/DatePicker';
import { ExportEntityButton } from '../../../../components/Monetize/ExportEntityButton';
import MEmptyDataPlaceholder from '../../../../components/Monetize/MEmptyDataPlaceholder';
import { RefreshTableButton } from '../../../../components/Monetize/RefreshTableButton';
import { getRateUsageBillingFrequencyDisplay } from '../../../../constants/offerings';
import { getSubscriptionOverviewRoute } from '../../../../constants/routes';
import { useSubscriptionUsage } from '../../../../hooks/AccountDetail/useSubscriptionUsage';
import { useUsageEventCustomFields } from '../../../../hooks/useUsageEventCustomFields';
import { useFlags } from '../../../../services/launchDarkly';
import { ManualUsageForm } from './ManualUsageForm';
import {
  collectPropertyKeys,
  mergePropertyColumnKeys,
} from './usageProperties.utils';
import { getUsagePropertyColumns } from './usagePropertyColumns';
import { UploadUsageModal } from './usageUpload/UploadUsageModal';

const columnHelper = createColumnHelper<IUsageEvent>();

function getColumns(
  usageTypes: IUsageTypeResSchema[],
  canUpdateUsage: boolean,
  isCanceled: boolean,
  onEdit: (record: IUsageEvent) => void,
  propertyKeys: string[],
  customFieldsByKey: Record<string, ICustomFieldResSchema>,
) {
  return [
    columnHelper.accessor('id', {
      ...getColHeaderWithSize('ID', 250),
      enableSorting: false,
    }),
    columnHelper.accessor('usageTypeId', {
      ...getColHeaderWithSize('Usage Type ID', 250),
      enableSorting: false,
    }),
    columnHelper.display({
      id: 'timestamp',
      ...getColHeaderWithSize('Timestamp (UTC)', 250),
      enableSorting: false,
      cell: ({ row }) => (
        <MText>
          {toDateTimeShort(fromUnixTime(Number(row.original.timestamp)), 'UTC')}
        </MText>
      ),
    }),
    columnHelper.display({
      id: 'unitsConsumed',
      ...getColHeaderWithSize('Units', 250),
      enableSorting: false,
      cell: ({ row }) => {
        const usageType = usageTypes.find(
          ({ id }) => id === row.original.usageTypeId,
        );
        if (!usageType) {
          return null;
        }
        return (
          <MText>
            {formatNumber(row.original.unitsConsumed, {
              minimumFractionDigits: usageType.decimalPlaces || 0,
              maximumFractionDigits: usageType.decimalPlaces || 0,
            })}{' '}
            {usageType.unitName}
          </MText>
        );
      },
    }),
    columnHelper.display({
      id: 'usageType',
      ...getColHeaderWithSize('Usage Type', 160),
      enableSorting: false,
      cell: ({ row }) => {
        const usageType = usageTypes.find(
          ({ id }) => id === row.original.usageTypeId,
        );
        if (!usageType) {
          return null;
        }
        return <MText>{usageType.name}</MText>;
      },
    }),
    ...getUsagePropertyColumns(columnHelper, propertyKeys, customFieldsByKey),
    columnHelper.display({
      id: 'actions',
      size: 32,
      enableSorting: false,
      cell: ({ row }) => {
        if (!canUpdateUsage) {
          return null;
        }
        return (
          <MFlex align="center" justify="flex-end">
            <DataTableActions
              actions={[
                {
                  title: 'Edit',
                  enabled: !isCanceled,
                  action: () => onEdit(row.original),
                },
              ]}
            />
          </MFlex>
        );
      },
    }),
  ];
}

export const SubscriptionUsageOverviewPage: FC = () => {
  const params = useParams();
  const accountId = params.accountId ?? '';
  const subscriptionId = params.subscriptionId ?? '';
  const productId = params.productId ?? '';

  const {
    formData,
    getUsageRecordsQuery,
    onOpenUsageForm,
    onCloseUsageForm,
    onOpenUsageUpload,
    onCloseUsageUpload,
    isUsageFormOpen,
    isManualUsageModalOpen,
    usageRecords,
    totalElements,
    filters,
    isCanceled,
    subscription,
    subscriptionItem,
    rate,
    isLoadingSubscription,
    activeUsageRecord,
    setActiveUsageRecord,
    canUpdateUsage,
    pager,
    earliestAllowedEditDate,
  } = useSubscriptionUsage({
    accountId,
    subscriptionId,
    productId,
  });

  const { enableUsageCustomFields } = useFlags();
  const { configuredKeys, customFieldsByKey } = useUsageEventCustomFields();
  const propertyKeys = useMemo(
    () =>
      enableUsageCustomFields
        ? mergePropertyColumnKeys(
            configuredKeys,
            collectPropertyKeys(usageRecords),
          )
        : [],
    [enableUsageCustomFields, configuredKeys, usageRecords],
  );
  const propertyKeysSignature = propertyKeys.join('|');

  const columns = useMemo(
    () =>
      getColumns(
        subscriptionItem?.product.usageTypes ?? [],
        canUpdateUsage,
        isCanceled,
        (record) => {
          setActiveUsageRecord(record);
          onOpenUsageForm();
        },
        propertyKeys,
        customFieldsByKey,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      subscriptionItem?.product.usageTypes,
      canUpdateUsage,
      isCanceled,
      propertyKeysSignature,
      customFieldsByKey,
    ],
  );

  if (!subscription || !subscriptionItem || !rate || isLoadingSubscription) {
    return <MPageLoader />;
  }
  const { control, formState } = formData;
  const {
    isLoading: isLoadingUsage,
    isFetchingNextPage,
    refetch,
    fetchNextPage,
  } = getUsageRecordsQuery;

  return (
    <MPageContainer maxHeight="100vh">
      {isManualUsageModalOpen && (
        <UploadUsageModal
          accountId={accountId}
          subscriptionId={subscriptionId}
          onClose={onCloseUsageUpload}
        />
      )}
      <MPageHeader
        hasBackButton
        parentLink={getSubscriptionOverviewRoute(accountId, subscriptionId)}
        parentLinkTitle={`${subscription.offering.name || ''} - ${
          rate.name || ''
        }`}
        title={`${subscriptionItem.product.name} - Training Details`}
        extraSubtitleParts={[
          <MText key={`subtitle-parts-1`}>
            Usage Frequency:{' '}
            {getRateUsageBillingFrequencyDisplay(rate.usageBillingFrequency)}
          </MText>,
        ]}
      />
      <MFlex direction="column" w={'100%'} mb="5">
        <MFlex justifyContent="space-between" alignItems="center">
          <Heading size="md" fontSize={18} lineHeight={1}>
            Usage Details
          </Heading>
          <MFlex alignItems="center">
            <MFormField
              error={formState?.errors?.startTime}
              label="From"
              isHorizontal
              labelProps={{ mr: 2 }}
            >
              <Controller
                name="startTime"
                control={control}
                render={({ field }) => (
                  <DatePicker includeTime onClose={refetch} {...field} />
                )}
              />
            </MFormField>
            <MFormField
              error={formState?.errors?.endTime}
              label="to"
              labelProps={{ fontWeight: 'normal', mx: 2 }}
              isHorizontal
            >
              <Controller
                name="endTime"
                control={control}
                render={({ field }) => (
                  <DatePicker includeTime onClose={refetch} {...field} />
                )}
              />
            </MFormField>
            {canUpdateUsage && !isCanceled && (
              <MButton
                ml={5}
                mr={2}
                variant="secondary"
                onClick={() => onOpenUsageForm()}
              >
                New Usage
              </MButton>
            )}
            <RefreshTableButton refetch={refetch} />
            <MBox>
              <ExportEntityButton
                entity="usage"
                applyInternalFiltersWithoutConfirmation
                internalFilters={filters}
                sortField={pager.sortField}
                sortOrder={pager.sortOrder}
                getFilename={() => `subscriptions-${accountId}.csv`}
              />
            </MBox>
            {canUpdateUsage && !isCanceled && (
              <DataTableActions
                actions={[
                  {
                    title: 'Import Usage',
                    icon: UploadIcon,
                    enabled: true,
                    action: () => onOpenUsageUpload(),
                  },
                ]}
              />
            )}
          </MFlex>
        </MFlex>
      </MFlex>
      {isUsageFormOpen && (
        <ManualUsageForm
          isOpen
          existingUsage={activeUsageRecord}
          onClose={() => {
            onCloseUsageForm();
            setActiveUsageRecord(undefined);
          }}
          subscriptionId={subscription.id}
          periodStartDate={subscription.periodStartDate}
          usageTypes={subscriptionItem.product.usageTypes}
          accountId={accountId}
          earliestAllowedEditDate={earliestAllowedEditDate}
        />
      )}

      <DataTable
        columns={columns}
        data={usageRecords}
        isLoading={isLoadingUsage}
        enablePagination={false}
        skipFilterValuePreprocessing
        emptyProps={{
          renderEmptyPlaceholder: () => (
            <MEmptyDataPlaceholder
              mainMessage="There aren't any usage records for this product."
              smallMessage={
                isCanceled
                  ? 'This subscription is canceled; no usage records can be added.'
                  : 'Add new usage records or change the date range.'
              }
              btnLabel={
                canUpdateUsage && !isCanceled ? 'Add New Usage' : undefined
              }
              onClick={onOpenUsageForm}
            />
          ),
        }}
        loadMoreProps={
          usageRecords.length > 0 ? { onLoadMore: fetchNextPage } : undefined
        }
        totalElements={totalElements}
      />
    </MPageContainer>
  );
};
